/**
 * Dark Mode Theme Provider
 * Manages dark mode state and applies theme classes
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "system";
    return (localStorage.getItem("theme") as Theme) || "system";
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "light";

    const stored = localStorage.getItem("theme") as Theme;
    if (stored === "dark") return "dark";
    if (stored === "light") return "light";

    // System preference
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark");

    // Apply resolved theme
    root.classList.add(resolvedTheme);

    // Update CSS custom properties for dark mode
    if (resolvedTheme === "dark") {
      root.style.setProperty("--color-bg-primary", "#1d3658"); // cello
      root.style.setProperty("--color-bg-secondary", "#2a4570"); // cello + 20%
      root.style.setProperty("--color-bg-tertiary", "#374e88"); // cello + 40%
      root.style.setProperty("--color-text-primary", "#f2faef"); // peppermint
      root.style.setProperty("--color-text-secondary", "rgba(242, 250, 239, 0.7)");
      root.style.setProperty("--color-text-tertiary", "rgba(242, 250, 239, 0.5)");
      root.style.setProperty("--color-primary", "#447a9c"); // wedgewood
      root.style.setProperty("--color-border", "rgba(255, 255, 255, 0.1)");
    } else {
      // Light mode - use existing CSS variables
      root.style.removeProperty("--color-bg-primary");
      root.style.removeProperty("--color-bg-secondary");
      root.style.removeProperty("--color-bg-tertiary");
      root.style.removeProperty("--color-text-primary");
      root.style.removeProperty("--color-text-secondary");
      root.style.removeProperty("--color-text-tertiary");
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-border");
    }
  }, [resolvedTheme]);

  useEffect(() => {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === "system") {
        setResolvedTheme(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);

    if (newTheme === "system") {
      const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setResolvedTheme(systemPrefersDark ? "dark" : "light");
    } else {
      setResolvedTheme(newTheme);
    }
  };

  const toggleTheme = () => {
    const newTheme = resolvedTheme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
